import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, forwardRef, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router } from '@angular/router';
import { Picto } from '@app/core/business/product/picto';
import { Weight } from '@app/core/business/weight';
import { CoreModule } from '@app/core/core.module';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, Observable, of, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, finalize, map, startWith, switchMap, tap, throttleTime } from 'rxjs/operators';
type ControlValue<T> = string | OptionItem<T>;

/**
 * Stratégie de requêtage lors de la saisie
 * - debounce : attendre la fin de la saisie
 * - throttle : requêter au début de la saisie, puis au bout de delayMs
 */
type Strategy = 'debounce' | 'throttle';

export interface OptionItem<T = any> {
  text: string;          // texte affiché
  img?: string | null;   // URL image (facultatif)
  data?: T;              // payload métier (facultatif)
  picto?: Picto | null;  // picto à droite (si fourni)
  action?: 'search';
  weight?: Weight;
  searchTerm?: string;

}


/**
 * Composant d’autocomplétion réutilisable.
 * - fetch(term) => Observable<OptionItem<T>[]> : fonction de récupération des options
 * - émet selected(option) ou enter(term)
 */
@Component({
  selector: 'app-autocomplete',
  standalone: true,
  templateUrl: './autocomplete.component.html',
  styleUrls: ['./autocomplete.component.scss'],
  host: {
    '[class.autocomplete-compact]': 'compactMode',
    '[class.autocomplete-classic]': '!compactMode'
  },
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => AutocompleteComponent),
      multi: true
    }
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    CoreModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatIconModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
})
export class AutocompleteComponent<T> implements OnDestroy, OnInit {
  private pendingFetchCount = 0; // Compteur de recherches en cours

  @Input() compactMode = false;
  /** (term) => Observable<OptionItem<T>[]> */
  @Input({ required: true }) fetch!: (term: string) => Observable<OptionItem<T>[]>;

  /** Apparence/styling */
  @Input() appearance: 'outline' | 'fill' | 'standard' = 'outline';
  @Input() stretch = true;           // width:100%
  @Input() borderless = false;       // supprime le chrome Material si true
  @Input() dense = true;                          // compacter la hauteur
  @Input() hideSubscript = true;                  // supprime l’espace des hints/erreurs
  @Input() showLabel = false;                     // cache le mat-label par défaut
  @Input() floatLabel: 'auto' | 'always' | 'never' = 'never'; // empêche le label de flotter



  /** UX */
  @Input() label = 'Recherche';
  @Input() placeholder = 'Rechercher…';
  @Input() minLength = 3;
  @Input() delayMs = 300;
  @Input() strategy: Strategy = 'debounce';
  /** Afficher un picto à droite (si dispo) */
  @Input() showRightPicto = true;
  @Input() showSearchOption = true;
  @Input() searchPrefix = 'Rechercher : ';
  /** Images */
  @Input() showImages = true;
  @Input() imgSize = 32;             // px
  @Input() imgRadius = 4;            // px
  @Input() placeholderImg: string | null = null; // fallback si 404

  /** Events */
  @Output() selected = new EventEmitter<T | OptionItem<T>>();
  @Output() enter = new EventEmitter<string>();

  @Input() clearOnNavigate = true;

  @ViewChild(MatAutocompleteTrigger) private trigger!: MatAutocompleteTrigger;
  @ViewChild('inputEl') private inputEl!: ElementRef<HTMLInputElement>;

  private suppressNextFetch = false;
  private suppressEnterOnce = false;
  private navSub?: Subscription;

  private optionsSubject = new BehaviorSubject<OptionItem<T>[]>([]);
  options$: Observable<OptionItem<T>[]> = this.optionsSubject.asObservable();

  control = new FormControl<ControlValue<T>>('' as ControlValue<T>);
  loading = false;
  private optionsSub?: Subscription;

  constructor(private router: Router, private cdr: ChangeDetectorRef, public readonly translateService: TranslateService) { }
  ngOnInit() {

    // réagir aux navigations pour vider le champ
    this.navSub = this.router.events
      .pipe(filter((e): e is NavigationEnd => e instanceof NavigationEnd))
      .subscribe(() => { if (this.clearOnNavigate) this.resetInput(); });

    const delayOp =
      this.strategy === 'throttle'
        ? (src: Observable<string>) => src // ou src.pipe(throttleTime(this.delayMs))
        : (src: Observable<string>) => src.pipe(debounceTime(this.delayMs));

    this.optionsSub?.unsubscribe();
    const pipeline$ = this.control.valueChanges.pipe(
      startWith(this.control.value ?? ''),

      filter((v): v is string => typeof v === 'string'),
      map(v => v.trim()),
      distinctUntilChanged(),
      delayOp as any,

      switchMap((term: string) => {
        if (term.length < this.minLength) {
          Promise.resolve().then(() => this.trigger?.closePanel());
          this.pendingFetchCount = 0; // Reset le compteur
          this.loading = false;

          return of([] as OptionItem<T>[]);
        }
        this.pendingFetchCount++; // Incrémente AVANT la requête

        this.loading = true;
        return this.fetch(term).pipe(
          finalize(() => {
            this.pendingFetchCount--;
            if (this.pendingFetchCount === 0) {
              this.loading = false;
            }
          }),
          map(arr => {
            const list = Array.isArray(arr) ? arr : [];
            if (!this.showSearchOption) return list;
            const searchOpt: OptionItem<T> = {
              text: `${this.searchPrefix}${term}`,
              action: 'search',
              searchTerm: term
            };
            return [searchOpt, ...list];
          })
        );
      })
    );

    this.optionsSub = pipeline$.subscribe(list => this.optionsSubject.next(list));
  }

  public startScan(): void {
    window.location.href = '/scan';
  }

  // appelé AVANT la sélection (clic souris)
  prepSelect() {
    this.suppressNextFetch = true;
  }

  private termOf = (v: any) => (typeof v === 'string' ? v : v?.text ?? '').trim();

  displayWith = (v?: string | OptionItem<T> | null) =>
    typeof v === 'string' ? v : (v?.text ?? '');


  /**
   * Gestion de la sélection d’une option
   */
  onOptionSelected(opt: OptionItem<T>) {
    if (opt?.action === 'search') {
      this.enter.emit(opt.searchTerm ?? '');
      this.resetInput();
      return;
    }
    this.selected.emit((opt?.data as T) ?? opt);
    this.control.setValue(opt as ControlValue<T>, { emitEvent: false });
    this.resetInput();
  }


  /**
   * Gestion de la touche Entrée
   * @param ev 
   * @returns 
   */
  onKeydownEnter(ev: KeyboardEvent) {
    const panelOpen = !!this.trigger?.panelOpen;
    const activeId = this.inputEl?.nativeElement.getAttribute('aria-activedescendant');
    const hasActiveOption = panelOpen && !!activeId;

    if (hasActiveOption) {
      return;
    }
    if (this.pendingFetchCount > 0) {
      ev.preventDefault();
      ev.stopPropagation();
      return;
    }

    const term = this.inputEl?.nativeElement.value.trim();

    ev.preventDefault();
    ev.stopPropagation();
    this.enter.emit(term);
    this.resetInput();
  }

  clear() {
    this.control.setValue('', { emitEvent: true });
    this.trigger?.closePanel();
  }


  /**
   * Vide immédiatement les options 
   */
  private flushOptionsSync() {
    // 1) pousse [] immédiatement
    this.optionsSubject.next([]);
    // 2) force l’UI à refléter [] avant toute action sur le panel
    this.cdr.detectChanges();
  }


  /**
   * Vide le champ et ferme le panel
   */
  private resetInput() {
    // vide la valeur sans relancer la pipeline
    this.control.setValue('' as any, { emitEvent: false });
    // flush synchrone des options (empêche l’overlay de recycler l’ancien contenu)
    this.flushOptionsSync();
    this.pendingFetchCount = 0;
    this.loading = false;
    // ferme le panel après que la vue soit à jour
    this.trigger?.closePanel();
    this.inputEl?.nativeElement.blur();
  }

  // appelé à chaque ouverture du panel
  onPanelOpened() {
    // si le terme est trop court, on s’assure que la liste est bien vide
    if (this.termOf(this.control.value).length < this.minLength) {
      this.flushOptionsSync();
    }
  }


  onImgErr(ev: Event) {
    if (!this.placeholderImg) return;
    const img = ev.target as HTMLImageElement;
    if (img && img.src !== this.placeholderImg) img.src = this.placeholderImg;
  }

  get belowThreshold(): boolean {
    const v = this.control.value;
    const term = typeof v === 'string' ? v.trim() : '';
    return term.length < this.minLength;
  }
  ngOnDestroy() {
    this.navSub?.unsubscribe();
    this.optionsSub?.unsubscribe();
  }

}





